import { useCallback, useMemo, useState } from 'react'

import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { CubeStatus, DisabledReasonType } from 'Cube/domain/cube.domain.types'
import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { usePermissions } from 'lib/rbac'
import { useQuoteApproval } from 'Cube/domain'
import { useApplyQuoteTemplate } from 'Cube/view/layouts/quote/hooks/useApplyQuoteTemplate'
import { AMENDMENT_QUOTE_UNAVAILABLE_REASON } from 'common/menuItems/quotes/quotes.constants'

type QuoteTemplate = DashboardApi20240730.GetQuoteTemplate.Response

enum AvailableModal {
  Archive = 'archive',
  DeleteLatestDraft = 'deleteLatestDraft',
  Revoke = 'revoke'
}

type UseQuoteEditorKebabMenu = () => {
  duplicateQuote: {
    visible: boolean
    disabled: boolean
    disabledReason?: string
    onClick: () => void
  }
  archiveQuote: {
    visible: boolean
    onClick: () => void
  }
  discardChanges: {
    visible: boolean
    onClick: () => void
  }
  revokeQuote: {
    visible: boolean
    onClick: () => void
  }
  saveAsTemplate: {
    onSuccess: (newTemplateId: string) => void
    disabled: boolean
    disabledReason?: string
  }
  applyQuoteTemplate: {
    onSelection: (template: QuoteTemplate) => void
    visible: boolean
    disabled: boolean
    disabledReason?: string
    hasSalesforceOpportunityId: boolean
  }
  modals: {
    [AvailableModal.Archive]: {
      active: boolean
      onClose: () => void
      onConfirm: () => Promise<void>
      submitting: boolean
      showSignatureWarning: boolean
    }
    [AvailableModal.DeleteLatestDraft]: {
      active: boolean
      onClose: () => void
      onConfirm: () => void
      submitting: boolean
    }
    [AvailableModal.Revoke]: {
      active: boolean
      onClose: () => void
      onConfirm: () => void
      submitting: boolean
    }
  }
  acceptQuote: {
    visible: boolean
  }
  cancelApproval: {
    visible: boolean
  }
  quoteId: string
}

export const useQuoteEditorKebabMenu: UseQuoteEditorKebabMenu = () => {
  const ctx = useCubeContext()
  const navigate = useNavigate()

  const [activeModal, setActiveModal] = useState<AvailableModal | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { can } = usePermissions()

  const modals = useMemo(
    () => ({
      [AvailableModal.Archive]: {
        active: activeModal === AvailableModal.Archive,
        submitting,
        onClose: () => setActiveModal(null),
        onConfirm: async () => {
          /** Reload the quote */
          await ctx.mutators.external.in.core()
          setActiveModal(null)
        },
        showSignatureWarning:
          ctx.queries.rawData.data.common.status === CubeStatus.QuoteReadyToSign
      },
      [AvailableModal.DeleteLatestDraft]: {
        active: activeModal === AvailableModal.DeleteLatestDraft,
        submitting,
        onClose: () => setActiveModal(null),
        onConfirm: async () => {
          setSubmitting(true)

          if (ctx.mutators.external.out.quote?.deleteLatestDraft) {
            await ctx.mutators.external.out.quote.deleteLatestDraft()
          }

          setSubmitting(false)
          setActiveModal(null)
        }
      },
      [AvailableModal.Revoke]: {
        active: activeModal === AvailableModal.Revoke,
        submitting,
        onClose: () => setActiveModal(null),
        onConfirm: () => {
          setActiveModal(null)
        }
      }
    }),
    [activeModal, ctx, submitting]
  )

  const duplicateQuoteOnClick = useCallback(() => {
    const onClick = async () => {
      await ctx.mutators.external.out.quote?.duplicate()
    }

    void onClick()
  }, [ctx.mutators.external.out.quote])

  const duplicateAvailable = ctx.queries.availableFeatures.quote.duplicate

  const duplicateQuote = useMemo(
    () => ({
      visible: can('org.quote:create') && duplicateAvailable.available.visible,
      disabled: !duplicateAvailable.available.enabled,
      disabledReason: match(duplicateAvailable.reasons[0]?.reasonType)
        .with(
          DisabledReasonType.CantChangeOnAmendmentDealType,
          () => AMENDMENT_QUOTE_UNAVAILABLE_REASON
        )
        .otherwise(() => undefined),
      onClick: duplicateQuoteOnClick
    }),
    [can, duplicateAvailable, duplicateQuoteOnClick]
  )

  const archiveQuoteOnClick = useCallback(() => {
    setActiveModal(AvailableModal.Archive)
  }, [])

  const isVisibleDiscardChanges = useMemo(
    () =>
      ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible,
    [ctx.queries.availableFeatures.quote.deleteLatestDraft.available.visible]
  )

  const archiveQuote = useMemo(
    () => ({
      visible:
        can('org.quote:archive') &&
        ctx.queries.availableFeatures.quote.archive.available.visible,
      onClick: archiveQuoteOnClick
    }),
    [
      can,
      archiveQuoteOnClick,
      ctx.queries.availableFeatures.quote.archive.available.visible
    ]
  )

  const applyQuoteTemplate = useApplyQuoteTemplate()

  const discardChangesOnClick = useCallback(() => {
    setActiveModal(AvailableModal.DeleteLatestDraft)
  }, [])

  const discardChanges = useMemo(
    () => ({
      visible: isVisibleDiscardChanges,
      onClick: discardChangesOnClick
    }),
    [discardChangesOnClick, isVisibleDiscardChanges]
  )

  const revokeQuoteOnClick = useCallback(() => {
    setActiveModal(AvailableModal.Revoke)
  }, [])

  const revokeQuote = useMemo(
    () => ({
      visible:
        can('org.quote:update') &&
        ctx.queries.availableFeatures.quote.revoke.available.visible,
      onClick: revokeQuoteOnClick
    }),
    [
      can,
      ctx.queries.availableFeatures.quote.revoke.available.visible,
      revokeQuoteOnClick
    ]
  )

  const isAmendment = ctx.queries.rawData.data.quote.dealType === 'AMENDMENT'

  const saveAsTemplate = {
    onSuccess: (newTemplateId: string) => {
      navigate(`/quote-templates/${newTemplateId}`)
    },
    disabled: isAmendment,
    disabledReason: isAmendment ? AMENDMENT_QUOTE_UNAVAILABLE_REASON : undefined
  }

  /**
   * The accept quote menu item is always visible when the accept action is available.
   * This ensures sales users have a consistent location to find the accept action.
   */
  const acceptQuote = useMemo(
    () => ({
      visible: ctx.queries.availableFeatures.quote.accept.available.visible
    }),
    [ctx.queries.availableFeatures.quote.accept.available.visible]
  )

  const { isApprovalPending } = useQuoteApproval()

  const cancelApproval = useMemo(
    () => ({
      visible: isApprovalPending
    }),
    [isApprovalPending]
  )

  return {
    duplicateQuote,
    archiveQuote,
    discardChanges,
    revokeQuote,
    saveAsTemplate,
    applyQuoteTemplate,
    modals,
    acceptQuote,
    cancelApproval,
    quoteId: ctx.queries.rawData.data.common.id
  }
}
